import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Check,
  Clock,
  ChefHat,
  Package,
  Truck,
  ArrowLeft,
  Phone,
  RefreshCw,
  AlertCircle,
  Loader2,
  XCircle,
} from "lucide-react";
import Header from "@/components/shared/Header";
import Footer from "@/components/shared/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { getSession } from "@/services/sessionStorage";
import { supabase } from "@/integrations/supabase/client";
import {
  useFirebaseOrderStatus,
  STORE_REJECTION_REASONS_EN,
  STORE_REJECTION_REASONS_AR,
  ORDER_REJECTION_REASONS_EN,
  ORDER_REJECTION_REASONS_AR,
  type OrderStatusCode,
  type FirebaseOrder,
} from "@/hooks/useFirebaseOrderStatus";

/* ── Happy-path tracking steps ── */
const STEPS: {
  statusCodes: OrderStatusCode[];
  icon: typeof Clock;
  labelEn: string;
  labelAr: string;
}[] = [
  {
    statusCodes: [0, 7],
    icon: Clock,
    labelEn: "Order Placed",
    labelAr: "تم الطلب",
  },
  {
    statusCodes: [2, 3],
    icon: ChefHat,
    labelEn: "Preparing",
    labelAr: "جاري التحضير",
  },
  { statusCodes: [4], icon: Package, labelEn: "Ready", labelAr: "جاهز" },
  {
    statusCodes: [5, 8],
    icon: Truck,
    labelEn: "On the Way",
    labelAr: "في الطريق",
  },
  {
    statusCodes: [6],
    icon: Check,
    labelEn: "Delivered",
    labelAr: "تم التوصيل",
  },
];

const STATUS_LABEL_EN: Record<OrderStatusCode, string> = {
  0: "Order Placed",
  1: "Rejected",
  2: "Accepted",
  3: "Preparing",
  4: "Ready",
  5: "On the Way",
  6: "Delivered",
  7: "Pending",
  8: "Handed to Driver",
};

const STATUS_LABEL_AR: Record<OrderStatusCode, string> = {
  0: "تم الطلب",
  1: "مرفوض",
  2: "مقبول",
  3: "جاري التحضير",
  4: "جاهز",
  5: "في الطريق",
  6: "تم التوصيل",
  7: "قيد الانتظار",
  8: "تم التسليم للسائق",
};

/* ── Map string status from .NET backend to numeric codes ── */
const STRING_STATUS_TO_CODE: Record<string, OrderStatusCode> = {
  // English status strings (case-insensitive matching done in function)
  new: 0,
  rejected: 1,
  accepted: 2,
  preparing: 3,
  onprogress: 3,
  "on-progress": 3,
  on_progress: 3,
  ready: 4,
  ondelivery: 5,
  "on-delivery": 5,
  on_delivery: 5,
  delivered: 6,
  pending: 7,
  deliveredtodelivery: 8,
  "delivered-to-delivery": 8,
  delivered_to_delivery: 8,
  handedtodriver: 8,
  "handed-to-driver": 8,
  handed_to_driver: 8,
};

function parseStatusToCode(
  status: string | number | null | undefined,
): OrderStatusCode {
  if (status === null || status === undefined) return 7; // pending

  // If already a number, validate and return
  if (typeof status === "number") {
    return status >= 0 && status <= 8 ? (status as OrderStatusCode) : 7;
  }

  // Convert string to lowercase and remove spaces for matching
  const normalized = String(status).toLowerCase().trim().replace(/\s+/g, "");

  // Check if it's a numeric string
  const numericValue = parseInt(normalized, 10);
  if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 8) {
    return numericValue as OrderStatusCode;
  }

  // Match against known string statuses
  return STRING_STATUS_TO_CODE[normalized] ?? 7;
}

type DbOrder = {
  id: string;
  order_number: string;
  status: string;
  total: number;
  items: any;
  created_at: string;
  firebase_path: string | null;
  restaurant_id: string | null;
  store_rejection_reason?: number | null;
  rejection_reason?: number | null;
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function getStepIndex(statusCode: OrderStatusCode): number {
  const idx = STEPS.findIndex((s) => s.statusCodes.includes(statusCode));
  return idx >= 0 ? idx : 0;
}

function sanitizeOrderNode(orderNumber?: string | null) {
  return orderNumber?.replace(/^#/, "").replace(/[.#$[\]]/g, "") ?? "";
}

function isUuid(value?: string | null) {
  return !!value && UUID_PATTERN.test(value);
}

function toOrderNumber(value?: string | null) {
  if (!value) return null;
  return value.startsWith("#") ? value : `#${value}`;
}

function asSafeNumber(value: unknown) {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatMoney(value: unknown) {
  return asSafeNumber(value).toFixed(2);
}

function getItemLineTotal(item: {
  quantity?: unknown;
  price?: unknown;
  unit_price?: unknown;
  total_price?: unknown;
}) {
  const quantity = Math.max(0, asSafeNumber(item.quantity));
  const explicitTotal = asSafeNumber(item.total_price);

  if (explicitTotal > 0) return explicitTotal;

  const unitPrice = asSafeNumber(item.price ?? item.unit_price);
  return unitPrice * quantity;
}

function parseFirebasePath(firebasePath?: string | null) {
  if (!firebasePath) return null;

  const match = firebasePath.match(
    /^projects\/([^/]+)\/branches\/([^/]+)\/orders\/([^/]+)$/,
  );
  if (!match) return null;

  return {
    projectId: match[1],
    branchId: match[2],
    orderId: match[3],
  };
}

export default function TrackPage() {
  const { language } = useLanguage();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const sessionSnapshot = useMemo(() => getSession(), []);
  const routeParamIsUuid = isUuid(orderId);
  const routeOrderNumber = routeParamIsUuid ? "" : sanitizeOrderNode(orderId);

  const [dbOrder, setDbOrder] = useState<DbOrder | null>(null);
  const [dbLoading, setDbLoading] = useState(true);

  useEffect(() => {
    if (!orderId) {
      setDbLoading(false);
      return;
    }

    const session = getSession();
    const customerId = (session.customer?.data?.id as string) || "";

    if (!customerId) {
      console.warn(
        "[Track] No customer id in session, cannot verify ownership",
      );
      setDbLoading(false);
      return;
    }

    (async () => {
      try {
        let query = supabase
          .from("orders")
          .select(
            "id, order_number, status, total, items, created_at, firebase_path, restaurant_id, store_rejection_reason, rejection_reason",
          )
          .eq("customer_id", customerId);

        if (routeParamIsUuid) {
          query = query.eq("id", orderId);
        } else {
          const withHash = orderId.startsWith("#") ? orderId : `#${orderId}`;
          const withoutHash = orderId.startsWith("#")
            ? orderId.slice(1)
            : orderId;
          query = query.or(
            `order_number.eq.${withHash},order_number.eq.${withoutHash}`,
          );
        }

        const { data: order } = await query.limit(1).maybeSingle();

        if (order) {
          setDbOrder(order as unknown as DbOrder);
        }
      } catch (err) {
        console.error("[Track] Order lookup error:", err);
      } finally {
        setDbLoading(false);
      }
    })();
  }, [orderId, routeParamIsUuid]);

  const sessionOrderNumber = sanitizeOrderNode(
    String(sessionSnapshot.customer.data?.last_order_number ?? ""),
  );
  const dbOrderNumber = sanitizeOrderNode(dbOrder?.order_number);
  const effectiveOrderNode =
    sessionOrderNumber || dbOrderNumber || routeOrderNumber;
  const sessionFirebasePath =
    sessionSnapshot.order.restaurantId &&
    sessionSnapshot.order.branchId &&
    effectiveOrderNode
      ? `projects/${sessionSnapshot.order.restaurantId}/branches/${sessionSnapshot.order.branchId}/orders/${effectiveOrderNode}`
      : undefined;
  const storedPathParts = parseFirebasePath(dbOrder?.firebase_path);
  const firebasePath =
    sessionFirebasePath ?? dbOrder?.firebase_path ?? undefined;

  useEffect(() => {
    if (!dbOrder && !orderId) return;

    console.group("[Firestore Track] Path resolution");
    console.log("routeOrderParam", orderId ?? null);
    console.log(
      "routeParamType",
      routeParamIsUuid ? "db-order-id" : "order-id",
    );
    console.log("routeOrderNode", routeOrderNumber || null);
    console.log("session.order", sessionSnapshot.order);
    console.log("session.customer.data", sessionSnapshot.customer.data);
    console.log("dbOrder", dbOrder);
    console.log("sessionOrderNumber", sessionOrderNumber || null);
    console.log("dbOrderNumber", dbOrderNumber || null);
    console.log("effectiveOrderNode", effectiveOrderNode || null);
    console.log("storedPathParts", storedPathParts);
    console.log("firebasePath(session)", sessionFirebasePath ?? null);
    console.log("firebasePath(db)", dbOrder?.firebase_path ?? null);
    console.log("firebasePath(selected)", firebasePath ?? null);
    console.log(
      "pathSource",
      sessionFirebasePath
        ? "session"
        : dbOrder?.firebase_path
          ? "database"
          : "none",
    );
    console.log(
      "firestore listen call",
      firebasePath ? `doc(firestoreDb, \"${firebasePath}\")` : null,
    );
    console.log(
      "firestore path segments",
      firebasePath ? firebasePath.split("/").filter(Boolean) : null,
    );

    if (
      !sessionSnapshot.order.restaurantId ||
      !sessionSnapshot.order.branchId
    ) {
      console.warn(
        "[Firestore Track] Session storage is missing restaurantId or branchId; falling back to stored firebase_path when available.",
      );
    }

    if (!sessionOrderNumber && !dbOrderNumber && routeOrderNumber) {
      console.warn(
        "[Firestore Track] Using route orderId as the Firestore document id because session storage and DB order_number are unavailable.",
      );
    } else if (!sessionOrderNumber) {
      console.warn(
        "[Firestore Track] Session storage does not include the placed order number; using DB/route fallback for the Firestore document id.",
      );
    }

    if (!dbOrder) {
      console.warn(
        "[Firestore Track] No order row was resolved from the database for this route parameter.",
      );
    }

    if (
      storedPathParts &&
      sessionSnapshot.order.restaurantId &&
      sessionSnapshot.order.branchId &&
      (storedPathParts.projectId !== sessionSnapshot.order.restaurantId ||
        storedPathParts.branchId !== sessionSnapshot.order.branchId)
    ) {
      console.warn(
        "[Firestore Track] MISMATCH between session storage IDs and stored firebase_path",
        {
          sessionRestaurantId: sessionSnapshot.order.restaurantId,
          sessionBranchId: sessionSnapshot.order.branchId,
          storedProjectId: storedPathParts.projectId,
          storedBranchId: storedPathParts.branchId,
        },
      );
    }

    console.groupEnd();
  }, [
    dbOrder,
    dbOrderNumber,
    firebasePath,
    orderId,
    routeOrderNumber,
    routeParamIsUuid,
    sessionFirebasePath,
    sessionOrderNumber,
    sessionSnapshot,
    storedPathParts,
  ]);

  const {
    order,
    loading: fbLoading,
    error,
    statusChanged,
    acknowledgeStatusChange,
  } = useFirebaseOrderStatus(firebasePath);

  // Re-fetch dbOrder from Supabase when Firebase status changes to Rejected (1)
  // This ensures we get the rejection reasons which are stored in Supabase, not Firebase
  useEffect(() => {
    if (!order || order.status !== 1 || !orderId) return;

    // Only re-fetch if we don't already have rejection reasons
    if (
      dbOrder?.store_rejection_reason != null ||
      dbOrder?.rejection_reason != null
    )
      return;

    const session = getSession();
    const customerId = (session.customer?.data?.id as string) || "";
    if (!customerId) return;

    (async () => {
      try {
        let query = supabase
          .from("orders")
          .select(
            "id, order_number, status, total, items, created_at, firebase_path, restaurant_id, store_rejection_reason, rejection_reason",
          )
          .eq("customer_id", customerId);

        if (routeParamIsUuid) {
          query = query.eq("id", orderId);
        } else {
          const withHash = orderId.startsWith("#") ? orderId : `#${orderId}`;
          const withoutHash = orderId.startsWith("#")
            ? orderId.slice(1)
            : orderId;
          query = query.or(
            `order_number.eq.${withHash},order_number.eq.${withoutHash}`,
          );
        }

        const { data: refreshedOrder } = await query.limit(1).maybeSingle();
        if (refreshedOrder) {
          setDbOrder(refreshedOrder as unknown as DbOrder);
        }
      } catch (err) {
        console.error(
          "[Track] Re-fetch order for rejection reasons error:",
          err,
        );
      }
    })();
  }, [
    order?.status,
    orderId,
    routeParamIsUuid,
    dbOrder?.store_rejection_reason,
    dbOrder?.rejection_reason,
  ]);

  useEffect(() => {
    if (!order) return;

    console.group("[Firestore Track] Monetary mapping");
    console.log("orderNumber", order.order_number);
    console.log("normalizedTotal", order.total);
    console.log("currencySymbol", order.currency_symbol ?? null);
    console.log(
      "items",
      order.items.map((item, index) => ({
        index,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.price,
        lineTotal: item.price * item.quantity,
      })),
    );
    console.groupEnd();
  }, [order]);

  /* ── Build effective order from Firebase or DB fallback ── */
  const effectiveOrder = useMemo(() => {
    // If we have Firebase order data, use it — Firebase is the real-time source of truth
    if (order) {
      return {
        ...order,
        // Prefer Firebase values, fallback to DB if Firebase doesn't have them
        store_rejection_reason:
          order.store_rejection_reason ?? dbOrder?.store_rejection_reason,
        order_rejection_reason:
          order.order_rejection_reason ?? dbOrder?.rejection_reason,
      };
    }

    // If we have DB order but no Firebase, create a compatible order object from DB data
    if (dbOrder) {
      const statusCode = parseStatusToCode(dbOrder.status);
      const dbItems = Array.isArray(dbOrder.items) ? dbOrder.items : [];

      return {
        status: statusCode,
        order_number: dbOrder.order_number,
        items: dbItems.map((item: any, index: number) => ({
          name: item.name || `Item ${index + 1}`,
          name_ar: item.name_ar,
          quantity: item.quantity || 1,
          price: item.unit_price || item.price || 0,
          total_price: item.total_price,
        })),
        total: dbOrder.total || 0,
        estimated_minutes: 25,
        placed_at: dbOrder.created_at,
        currency_symbol: "$",
        store_rejection_reason: dbOrder.store_rejection_reason ?? undefined,
        order_rejection_reason: dbOrder.rejection_reason ?? undefined,
      } as FirebaseOrder;
    }

    return null;
  }, [order, dbOrder]);

  const loading = dbLoading || fbLoading;
  const isRTL = language === "ar";
  const isRejected = effectiveOrder?.status === 1;
  const isDelivered = effectiveOrder?.status === 6;

  // Auto-update elapsed time every minute
  const [currentTime, setCurrentTime] = useState(Date.now());
  useEffect(() => {
    // Don't update if order is delivered or rejected (final states)
    if (isDelivered || isRejected) return;

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [isDelivered, isRejected]);

  /* ── Status-change notification ── */
  useEffect(() => {
    if (!statusChanged || !order) return;
    acknowledgeStatusChange();

    const label =
      language === "ar"
        ? STATUS_LABEL_AR[order.status]
        : STATUS_LABEL_EN[order.status];

    if (order.status === 1) {
      const isAr = language === "ar";
      const storeR = effectiveOrder?.store_rejection_reason;
      const orderR = effectiveOrder?.order_rejection_reason;
      const parts: string[] = [];
      if (storeR != null && storeR !== 0)
        parts.push(
          isAr
            ? (STORE_REJECTION_REASONS_AR[storeR] ?? "")
            : (STORE_REJECTION_REASONS_EN[storeR] ?? ""),
        );
      if (orderR != null && orderR !== -1)
        parts.push(
          isAr
            ? (ORDER_REJECTION_REASONS_AR[orderR] ?? "")
            : (ORDER_REJECTION_REASONS_EN[orderR] ?? ""),
        );
      const reason = parts.filter(Boolean).join(" / ");
      toast({
        variant: "destructive",
        title: isAr ? "تم رفض الطلب" : "Order Rejected",
        description: reason || undefined,
      });
    } else if (order.status === 6) {
      toast({
        title: language === "ar" ? "بالعافية! 🎉" : "Enjoy your meal! 🎉",
        description: label,
      });
    } else {
      toast({
        title: language === "ar" ? "تحديث حالة الطلب" : "Order Status Update",
        description: label,
      });
    }
  }, [statusChanged, order, language]);

  /* ── Loading ── */
  if (loading || dbLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Determine if we're in "waiting for Firebase" state ── */
  // Only show waiting state if:
  // 1. We have a DB order
  // 2. Firebase path exists (meaning we expect Firebase data)
  // 3. But Firebase hasn't returned data yet
  const isWaitingForFirebase = !order && dbOrder && firebasePath && !error;

  /* ── Order exists in DB but waiting for Firebase confirmation ── */
  if (isWaitingForFirebase) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6 max-w-2xl">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold text-foreground">
                {language === "ar" ? "تتبع الطلب" : "Track Order"}{" "}
                {dbOrder.order_number}
              </h1>
            </div>
          </div>

          <div className="text-center py-16 space-y-4">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary mx-auto">
              <Clock className="h-10 w-10 animate-pulse" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {language === "ar"
                ? "بانتظار تأكيد المطعم"
                : "Waiting for restaurant confirmation"}
            </h2>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              {language === "ar"
                ? "تم استلام طلبك بنجاح وسيتم تحديث الحالة تلقائياً بمجرد تأكيد المطعم"
                : "Your order has been received and the status will update automatically once the restaurant confirms it"}
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {language === "ar"
                ? "يتم التحديث تلقائياً..."
                : "Auto-refreshing..."}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Error / not found ── */
  if (!effectiveOrder) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="font-heading text-xl font-bold text-foreground">
              {language === "ar" ? "الطلب غير موجود" : "Order not found"}
            </h2>
            <p className="text-muted-foreground text-sm">
              {language === "ar"
                ? "تعذّر العثور على الطلب المطلوب"
                : "We couldn't find this order"}
            </p>
            <Button onClick={() => navigate("/track")} variant="outline">
              {language === "ar" ? "البحث عن طلب" : "Search for an order"}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  /* ── Derived values ── */
  const currentStepIndex = getStepIndex(effectiveOrder.status);
  const progressPercent = isRejected
    ? 0
    : ((currentStepIndex + 1) / STEPS.length) * 100;

  const placedAt = effectiveOrder.placed_at
    ? new Date(effectiveOrder.placed_at)
    : new Date();
  const minutesElapsed = Math.floor((currentTime - placedAt.getTime()) / 60000);
  const minutesRemaining = Math.max(
    0,
    (effectiveOrder.estimated_minutes || 25) - minutesElapsed,
  );

  const currencySymbol = effectiveOrder.currency_symbol || "$";

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 max-w-2xl">
        {/* Back button + title */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">
              {language === "ar" ? "تتبع الطلب" : "Track Order"}{" "}
              {effectiveOrder.order_number}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === "ar" ? "تم الطلب قبل" : "Placed"} {minutesElapsed}{" "}
              {language === "ar" ? "دقيقة" : "min ago"}
            </p>
          </div>
        </div>

        {/* ── Rejected Banner ── */}
        {isRejected && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5">
            <CardContent className="py-6">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 shrink-0 rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <h2 className="font-heading text-lg font-bold text-destructive mb-1">
                    {language === "ar" ? "تم رفض الطلب" : "Order Rejected"}
                  </h2>
                  {(() => {
                    const isAr = language === "ar";
                    const storeR = effectiveOrder.store_rejection_reason;
                    const orderR = effectiveOrder.order_rejection_reason;
                    const parts: string[] = [];
                    if (storeR != null && storeR !== 0)
                      parts.push(
                        isAr
                          ? (STORE_REJECTION_REASONS_AR[storeR] ?? "")
                          : (STORE_REJECTION_REASONS_EN[storeR] ?? ""),
                      );
                    if (orderR != null && orderR !== -1)
                      parts.push(
                        isAr
                          ? (ORDER_REJECTION_REASONS_AR[orderR] ?? "")
                          : (ORDER_REJECTION_REASONS_EN[orderR] ?? ""),
                      );
                    const rejectionText = parts.filter(Boolean).join(" / ");
                    return rejectionText ? (
                      <p className="text-sm text-muted-foreground">
                        {rejectionText}
                      </p>
                    ) : null;
                  })()}

                  {/* ── Out-of-stock item list (store_rejection_reason === 1) ── */}
                  {effectiveOrder.store_rejection_reason === 1 &&
                    Array.isArray(effectiveOrder.out_of_stock_items) &&
                    effectiveOrder.out_of_stock_items.length > 0 &&
                    (() => {
                      const outItems = effectiveOrder.out_of_stock_items
                        .map((idx) => effectiveOrder.items[idx])
                        .filter(Boolean);

                      if (!outItems.length) return null;

                      return (
                        <div className="mt-3 rounded-lg border border-destructive/20 bg-background overflow-hidden">
                          <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {language === "ar"
                              ? "الأصناف غير المتوفرة"
                              : "Unavailable items"}
                          </p>
                          <ul className="divide-y divide-border">
                            {outItems.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-center gap-3 px-3 py-2.5"
                              >
                                <span className="h-2 w-2 rounded-full bg-destructive shrink-0" />
                                <span className="flex-1 text-sm text-foreground">
                                  {language === "ar" && item.name_ar
                                    ? item.name_ar
                                    : item.name}
                                </span>
                                <span className="text-sm text-muted-foreground tabular-nums">
                                  ×{item.quantity}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}

                  {effectiveOrder.rejection_note && (
                    <p className="text-sm text-muted-foreground mt-1 italic">
                      "{effectiveOrder.rejection_note}"
                    </p>
                  )}
                  <Button
                    onClick={() => navigate("/menu")}
                    className="mt-4 gap-2"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4" />
                    {language === "ar" ? "اطلب مرة أخرى" : "Order Again"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Status Card (non-rejected) ── */}
        {!isRejected && (
          <Card className="mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {(() => {
                    const StepIcon = STEPS[currentStepIndex].icon;
                    return <StepIcon className="h-6 w-6 text-primary" />;
                  })()}
                  <span className="font-heading text-xl font-bold text-foreground">
                    {language === "ar"
                      ? STATUS_LABEL_AR[effectiveOrder.status]
                      : STATUS_LABEL_EN[effectiveOrder.status]}
                  </span>
                </div>
                <Badge variant="secondary" className="text-sm">
                  {isDelivered
                    ? language === "ar"
                      ? "مكتمل ✓"
                      : "Done ✓"
                    : `~${minutesRemaining} ${language === "ar" ? "د" : "min"}`}
                </Badge>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Stepper */}
            <CardContent className="pt-6">
              <div className="relative">
                {STEPS.map((step, idx) => {
                  const isActive = idx <= currentStepIndex;
                  const isCurrent = idx === currentStepIndex;
                  const StepIcon = step.icon;

                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-4 mb-6 last:mb-0"
                    >
                      <div className="flex flex-col items-center">
                        <div
                          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-500 ${
                            isCurrent
                              ? "border-primary bg-primary text-primary-foreground scale-110 shadow-lg shadow-primary/30"
                              : isActive
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border bg-muted text-muted-foreground"
                          }`}
                        >
                          {isActive && idx < currentStepIndex ? (
                            <Check className="h-5 w-5" />
                          ) : (
                            <StepIcon className="h-5 w-5" />
                          )}
                        </div>
                        {idx < STEPS.length - 1 && (
                          <div
                            className={`w-0.5 h-8 mt-1 transition-colors duration-500 ${
                              idx < currentStepIndex
                                ? "bg-primary"
                                : "bg-border"
                            }`}
                          />
                        )}
                      </div>
                      <div className="pt-2">
                        <p
                          className={`font-medium text-sm ${
                            isCurrent
                              ? "text-primary font-bold"
                              : isActive
                                ? "text-foreground"
                                : "text-muted-foreground"
                          }`}
                        >
                          {language === "ar" ? step.labelAr : step.labelEn}
                        </p>
                        {isCurrent && !isDelivered && (
                          <p className="text-xs text-muted-foreground mt-0.5 animate-pulse">
                            {language === "ar" ? "الآن..." : "In progress..."}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ETA Countdown */}
        {!isRejected && !isDelivered && (
          <Card className="mb-6">
            <CardContent className="py-6 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {language === "ar"
                    ? "الوقت المتبقي المتوقع"
                    : "Estimated time remaining"}
                </p>
                <p className="font-heading text-3xl font-bold text-foreground tabular-nums">
                  {minutesRemaining > 0 ? (
                    <>
                      {String(minutesRemaining).padStart(2, "0")}
                      <span className="text-lg text-muted-foreground ml-1">
                        {language === "ar" ? "دقيقة" : "min"}
                      </span>
                    </>
                  ) : (
                    <span className="text-primary">
                      {language === "ar" ? "أي لحظة!" : "Any moment!"}
                    </span>
                  )}
                </p>
              </div>
              <div className="relative">
                <div className="h-16 w-16 rounded-full border-4 border-primary/20 flex items-center justify-center">
                  <div
                    className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin"
                    style={{ animationDuration: "3s" }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Delivery Driver Info */}
        {(effectiveOrder.status === 5 || effectiveOrder.status === 8) &&
          effectiveOrder.driver_name && (
            <Card className="mb-6 border-primary/30">
              <CardContent className="py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Truck className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">
                      {effectiveOrder.driver_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {language === "ar" ? "سائق التوصيل" : "Delivery Driver"}
                    </p>
                  </div>
                </div>
                {effectiveOrder.driver_phone && (
                  <Button variant="outline" size="sm" className="gap-2" asChild>
                    <a href={`tel:${effectiveOrder.driver_phone}`}>
                      <Phone className="h-4 w-4" />
                      {language === "ar" ? "اتصل" : "Call"}
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

        {/* Order Details */}
        {effectiveOrder.items && effectiveOrder.items.length > 0 && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <h3 className="font-heading font-semibold text-foreground mb-3">
                {language === "ar" ? "تفاصيل الطلب" : "Order Details"}
              </h3>
              <div className="space-y-2">
                {effectiveOrder.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-foreground">
                      {item.quantity}x{" "}
                      {language === "ar" && item.name_ar
                        ? item.name_ar
                        : item.name}
                    </span>
                    <span className="text-muted-foreground">
                      {currencySymbol}
                      {formatMoney(getItemLineTotal(item))}
                    </span>
                  </div>
                ))}
                <div className="border-t border-border pt-2 mt-2 flex items-center justify-between font-bold">
                  <span className="text-foreground">
                    {language === "ar" ? "الإجمالي" : "Total"}
                  </span>
                  <span className="text-primary">
                    {currencySymbol}
                    {formatMoney(effectiveOrder.total)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed state */}
        {isDelivered && (
          <div className="text-center py-6 space-y-4">
            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary mx-auto">
              <Check className="h-10 w-10" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">
              {language === "ar" ? "بالعافية! 🎉" : "Enjoy your meal! 🎉"}
            </h2>
            <Button onClick={() => navigate("/menu")} className="gap-2">
              {language === "ar" ? "اطلب مرة أخرى" : "Order Again"}
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
